/**
 * Notification preference definitions — the single source of truth for both
 * platforms and the Cloud Functions that read these values.
 *
 * This table previously existed in three places (the mobile Settings screen, an
 * unused mobile component, and nowhere at all on web), which is how the mobile
 * screen ended up shipping a "Custody Handoffs" toggle that no function read.
 *
 * Platform-neutral on purpose: no icons, no styles. Icon names differ per
 * platform (Ionicons vs react-icons), so each surface maps `key` to its own
 * icon and renders with its own design system.
 *
 * ─── Default semantics ──────────────────────────────────────────────────────
 *
 * `defaultValue` is what applies when the user has never touched the toggle.
 *
 * Everything is opt-OUT (default true) EXCEPT `handoffReminder`. That one is
 * the only notification the product sends proactively — it arrives on a
 * schedule rather than in response to the co-parent doing something — so it
 * stays silent until the user asks for it.
 *
 * The Cloud Function (functions/reminders/handoffReminder.js) independently
 * requires the stored value to be exactly `true`, so an absent key can never be
 * read as consent. Changing `defaultValue` here does NOT opt anyone in; the
 * function's `=== true` check is the real gate.
 *
 * ─── Writing these values ───────────────────────────────────────────────────
 *
 * Always write with `notificationPrefPath(key)` — a dotted field path that
 * merges. Assigning the whole `notificationPrefs` map replaces it and silently
 * drops every key the writing screen doesn't render, which is a bug this
 * codebase has already shipped once.
 */

/**
 * @typedef {Object} NotificationPrefDef
 * @property {string}  key           Firestore key under `users/{uid}.notificationPrefs`
 * @property {string}  label         Short UI label
 * @property {string}  description   One-line explanation shown under the label
 * @property {boolean} defaultValue  Value when the user has never set it
 * @property {'push'|'email'} channel How it is delivered
 */

/** @type {NotificationPrefDef[]} */
export const NOTIFICATION_PREFS = [
  {
    key: 'messages',
    label: 'Messages',
    description: 'New messages from your co-parent',
    defaultValue: true,
    channel: 'push',
  },
  {
    key: 'swaps',
    label: 'Swap Requests',
    description: 'Custody swap proposals and responses',
    defaultValue: true,
    channel: 'push',
  },
  {
    key: 'expenses',
    label: 'Expenses',
    description: 'New expenses and settlements',
    defaultValue: true,
    channel: 'push',
  },
  {
    key: 'activities',
    label: 'Activities',
    description: 'Activity reminders and changes',
    defaultValue: true,
    channel: 'push',
  },
  {
    key: 'handoffReminder',
    label: 'Handoff Reminders',
    description: 'An evening reminder the night before a changeover day',
    defaultValue: false,
    channel: 'push',
  },
  {
    key: 'email',
    label: 'Email updates',
    description: 'Occasional setup tips and product updates',
    defaultValue: true,
    channel: 'email',
  },
];

/** Preference definitions delivered over push (mobile only — web has no push). */
export const PUSH_NOTIFICATION_PREFS = NOTIFICATION_PREFS.filter((p) => p.channel === 'push');

/** Preference definitions delivered over email (relevant on every platform). */
export const EMAIL_NOTIFICATION_PREFS = NOTIFICATION_PREFS.filter((p) => p.channel === 'email');

/** Map of key -> defaultValue, for seeding local UI state. */
export const DEFAULT_NOTIFICATION_PREFS = NOTIFICATION_PREFS.reduce((acc, p) => {
  acc[p.key] = p.defaultValue;
  return acc;
}, {});

/**
 * The `notificationPrefs` map written into a brand-new user document at account
 * creation. THE single source of truth: every signup path must write this exact
 * shape so a new user's stored doc matches the toggles the Settings screens
 * render (previously three hand-maintained copies had drifted to a stale schema
 * — `scheduleChanges` / `newExpenses` — that no Cloud Function reads).
 *
 * It is the per-type UI defaults plus two keys that intentionally have no
 * Settings row:
 *   - `push`      Global push kill-switch, default on. Honored by every push
 *                 path — the reactive fan-out (functions/expoPush.js) and the
 *                 handoff reminder (functions/reminders/handoffReminder.js).
 *   - `reminders` The co-parent-joined notice (functions/invites/notifyAccepted)
 *                 reuses this key; opt-out like the reactive types.
 *
 * The Cloud Functions runtime is CommonJS and cannot import this ESM module, so
 * `functions/notificationDefaults.js` mirrors it. A differential test in the
 * mobile Jest suite (packages/mobile/__tests__/utils/notificationDefaults.test.js)
 * fails if the two ever drift.
 */
export function buildSignupNotificationPrefs() {
  return {
    ...DEFAULT_NOTIFICATION_PREFS,
    push: true,
    reminders: true,
  };
}

/**
 * Resolve the effective value of one preference.
 *
 * Strict boolean comparison, not truthiness: a stored `"true"` string or `1` is
 * treated as unset and falls back to the default, matching the Cloud Function's
 * `=== true` gate. Without this a malformed write could read as consent on the
 * client while the server correctly refuses to send — a confusing split where
 * the UI claims a notification is on and it never arrives.
 *
 * @param {object|null|undefined} prefs  The user's notificationPrefs map
 * @param {string} key
 * @returns {boolean}
 */
export function resolveNotificationPref(prefs, key) {
  const stored = prefs ? prefs[key] : undefined;
  if (stored === true) return true;
  if (stored === false) return false;
  return DEFAULT_NOTIFICATION_PREFS[key] === true;
}

/**
 * Merge stored prefs over the defaults so every known key has a boolean.
 * Unknown stored keys are preserved — a stale key from an older client should
 * not be dropped by a screen that doesn't render it.
 */
export function withNotificationDefaults(prefs) {
  const out = { ...DEFAULT_NOTIFICATION_PREFS, ...(prefs || {}) };
  for (const { key } of NOTIFICATION_PREFS) {
    out[key] = resolveNotificationPref(prefs, key);
  }
  return out;
}

/**
 * Firestore field path for a single preference. Use this for every write.
 * @param {string} key
 * @returns {string}
 */
export function notificationPrefPath(key) {
  return `notificationPrefs.${key}`;
}
